import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    TextField,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    IconButton,
    Card,
    CardContent,
    Divider,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DownloadIcon from '@mui/icons-material/Download';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('createdAt'); // Default sort by date
    const [sortDirection, setSortDirection] = useState('desc'); // Default sort direction
    const [testNameFilter, setTestNameFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [uniqueTestNames, setUniqueTestNames] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);

    useEffect(() => {
        fetchOrders();
    }, []);

    useEffect(() => {
        filterOrders();
    }, [searchTerm, orders, sortBy, sortDirection, testNameFilter, startDate, endDate]);
    
    // Extract unique test names for the filter dropdown
    useEffect(() => {
        if (orders.length > 0) {
            const testNamesSet = new Set();
            
            orders.forEach(order => {
                if (order.cart && order.cart.length > 0) {
                    order.cart.forEach(item => {
                        if (item.name) {
                            testNamesSet.add(item.name);
                        }
                    });
                }
            });
            
            const names = Array.from(testNamesSet).sort();
            setUniqueTestNames(names);
        }
    }, [orders]);

    const filterOrders = () => {
        let filtered = orders;

        // Text search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            filtered = filtered.filter((order) => {
                const orderId = (order._id || '').toLowerCase();
                const firstName = (order.user?.firstName || '').toLowerCase();
                const lastName = (order.user?.lastName || '').toLowerCase();
                const email = (order.user?.email || '').toLowerCase();
                const fullName = `${firstName} ${lastName}`.toLowerCase();
                const total = order.total?.toString() || '';
                
                // Check product names in the cart for matches
                const productNameMatches = order.cart && order.cart.some(item => 
                    (item.name || '').toLowerCase().includes(searchLower)
                );

                return (
                    orderId.includes(searchLower) ||
                    firstName.includes(searchLower) ||
                    lastName.includes(searchLower) ||
                    fullName.includes(searchLower) ||
                    email.includes(searchLower) ||
                    total.includes(searchLower) ||
                    productNameMatches
                );
            });
        }
        
        // Specific test name filter
        if (testNameFilter) {
            filtered = filtered.filter(order => 
                order.cart && order.cart.some(item => item.name === testNameFilter)
            );
        }
        
        // Date range filter - start date
        if (startDate) {
            const startDateTime = new Date(startDate);
            startDateTime.setHours(0, 0, 0, 0); // Start of the day
            filtered = filtered.filter(order => {
                const orderDate = new Date(order.createdAt);
                return orderDate >= startDateTime;
            });
        }
        
        // Date range filter - end date
        if (endDate) {
            const endDateTime = new Date(endDate);
            endDateTime.setHours(23, 59, 59, 999); // End of the day
            filtered = filtered.filter(order => {
                const orderDate = new Date(order.createdAt);
                return orderDate <= endDateTime;
            });
        }

        // Apply sorting based on selected sort field and direction
        filtered.sort((a, b) => {
            if (sortBy === 'productName') {
                // Sort by first product name
                const productNameA = a.cart && a.cart.length > 0 ? (a.cart[0].name || '').toLowerCase() : '';
                const productNameB = b.cart && b.cart.length > 0 ? (b.cart[0].name || '').toLowerCase() : '';
                
                return sortDirection === 'asc' 
                    ? productNameA.localeCompare(productNameB)
                    : productNameB.localeCompare(productNameA);
            } else if (sortBy === 'total') {
                return sortDirection === 'asc' 
                    ? a.total - b.total
                    : b.total - a.total;
            } else {
                // Default sort by date
                return sortDirection === 'asc'
                    ? new Date(a.createdAt) - new Date(b.createdAt)
                    : new Date(b.createdAt) - new Date(a.createdAt);
            }
        });
        
        setFilteredOrders(filtered);
    };

    const fetchOrders = async (pageNum = 1) => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/orders`, {
                params: { page: pageNum, limit: 20 },
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const newOrders = response.data.data.data;

            if (pageNum === 1) {
                setOrders(newOrders);
            } else {
                setOrders((prevOrders) => [...prevOrders, ...newOrders]);
            }
            setHasMore(newOrders.length === 20);
            setPage(pageNum);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch orders. Please try again.');
            setLoading(false);
        }
    };

    // Rest of the component remains the same
    const loadMoreOrders = () => {
        if (hasMore && !loading) {
            fetchOrders(page + 1);
        }
    };

    const handleExtendSubscription = async (subscriptionId) => {
        try {
            await axios.patch(
                `${process.env.REACT_APP_API_URL}/api/subscriptions/extendSubscription/${subscriptionId}`,
                {},
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                },
            );
            fetchOrders();
            setOpenDialog(false);
        } catch (err) {
            setError('Failed to extend subscription. Please try again.');
        }
    };

    const handleOpenDialog = (order) => {
        setSelectedOrder(order);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setSelectedOrder(null);
        setOpenDialog(false);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSortChange = (event) => {
        setSortBy(event.target.value);
    };

    const toggleSortDirection = () => {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    };
    
    const handleTestNameFilterChange = (event) => {
        setTestNameFilter(event.target.value);
    };
    
    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
    };
    
    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
    };
    
    const handleClearFilters = () => {
        setTestNameFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    };
    
    // Calculate stats from filtered orders
    const getOrderStats = () => {
        if (!filteredOrders.length) return { count: 0, total: 0 };
        
        const total = filteredOrders.reduce((sum, order) => sum + (order.total || 0), 0);
        
        return {
            count: filteredOrders.length,
            total: total.toFixed(2)
        };
    };
    
    const orderStats = getOrderStats();

    const handleExport = async (format) => {
        try {
            setExportLoading(true);
            
            // Build query parameters
            const params = new URLSearchParams();
            params.append('format', format);
            
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (testNameFilter) params.append('testName', testNameFilter);
            
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/orders/export?${params.toString()}`,
                {
                    headers: { 
                        Authorization: `Bearer ${localStorage.getItem('token')}` 
                    },
                    responseType: format === 'csv' ? 'blob' : 'json'
                }
            );
            
            if (format === 'csv') {
                // Create download link for CSV
                const blob = new Blob([response.data], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `orders_export_${new Date().toISOString().split('T')[0]}.csv`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                // Handle JSON export
                const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `orders_export_${new Date().toISOString().split('T')[0]}.json`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
            
            setExportLoading(false);
        } catch (err) {
            console.error('Export error:', err);
            setError('Failed to export data. Please try again.');
            setExportLoading(false);
        }
    };

    if (loading && orders.length === 0) return <CircularProgress />;

    return (
        <div style={{ padding: '20px' }}>
            <Typography variant="h4" gutterBottom>
                Order Management
            </Typography>
            
            {/* Summary Stats Card with Export */}
            <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)', color: 'white' }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ReceiptLongIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                                <Box>
                                    <Typography variant="h6">
                                        Order Summary
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>{orderStats.count}</strong> orders found
                                        {testNameFilter && ` for "${testNameFilter}"`}
                                    </Typography>
                                    {(startDate || endDate) && (
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            {startDate && `From ${new Date(startDate).toLocaleDateString()}`}
                                            {startDate && endDate && ' - '}
                                            {endDate && `To ${new Date(endDate).toLocaleDateString()}`}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                    ${orderStats.total}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    Total Revenue
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<GroupIcon />}
                                    onClick={() => handleExport('csv')}
                                    disabled={exportLoading || filteredOrders.length === 0}
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        },
                                    }}
                                >
                                    Export Customers (CSV)
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => handleExport('json')}
                                    disabled={exportLoading || filteredOrders.length === 0}
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        },
                                    }}
                                >
                                    Export All (JSON)
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Search and Basic Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Search by test name, customer name, email, or total"
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{ backgroundColor: 'white' }}
                        placeholder="Type to search..."
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined" sx={{ backgroundColor: 'white' }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select
                            value={sortBy}
                            onChange={handleSortChange}
                            label="Sort By"
                        >
                            <MenuItem value="createdAt">Date</MenuItem>
                            <MenuItem value="productName">Test/Product Name</MenuItem>
                            <MenuItem value="total">Total</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button 
                        variant="outlined" 
                        startIcon={sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                        onClick={toggleSortDirection}
                        fullWidth
                    >
                        {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                    </Button>
                </Grid>
            </Grid>
            
            {/* Advanced Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined" sx={{ backgroundColor: 'white' }}>
                        <InputLabel id="test-filter-label">Filter by Test/Product</InputLabel>
                        <Select
                            labelId="test-filter-label"
                            value={testNameFilter}
                            onChange={handleTestNameFilterChange}
                            label="Filter by Test/Product"
                        >
                            <MenuItem value="">
                                <em>All Tests/Products</em>
                            </MenuItem>
                            {uniqueTestNames.map((name) => (
                                <MenuItem key={name} value={name} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ backgroundColor: 'white' }}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={handleEndDateChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ backgroundColor: 'white' }}
                    />
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button 
                        variant="outlined" 
                        color="secondary"
                        onClick={handleClearFilters}
                        fullWidth
                    >
                        Clear Filters
                    </Button>
                </Grid>
            </Grid>

            {error && (
                <Alert severity="error" style={{ marginBottom: '20px' }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Test/Product</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredOrders.map((order) => {
                            // Get the first product name from the cart
                            const primaryProduct = order.cart && order.cart.length > 0 ? order.cart[0].name : 'N/A';
                            // Show indication if there are multiple products
                            const productDisplay = order.cart && order.cart.length > 1 
                                ? `${primaryProduct} +${order.cart.length - 1} more` 
                                : primaryProduct;
                            
                            return (
                                <TableRow key={order._id}>
                                    <TableCell>{productDisplay}</TableCell>
                                    <TableCell>
                                        {order.user?.firstName} {order.user?.lastName}
                                    </TableCell>
                                    <TableCell>{order.user?.email}</TableCell>
                                    <TableCell>${order.total}</TableCell>
                                    <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <Button onClick={() => handleOpenDialog(order)}>View Details</Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {hasMore && filteredOrders.length === orders.length && (
                <Button onClick={loadMoreOrders} disabled={loading} style={{ marginTop: '20px' }}>
                    {loading ? 'Loading...' : 'Load More Orders'}
                </Button>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Order Details</DialogTitle>
                <DialogContent>
                    {selectedOrder && (
                        <div>
                            <Typography>Order ID: {selectedOrder._id}</Typography>
                            <Typography>
                                User: {selectedOrder.user?.firstName} {selectedOrder.user?.lastName}
                            </Typography>
                            <Typography>Email: {selectedOrder.user?.email}</Typography>
                            <Typography>Phone: {selectedOrder.phone}</Typography>
                            <Typography>Total: ${selectedOrder.total}</Typography>
                            <Typography>Date: {new Date(selectedOrder.createdAt).toLocaleDateString()}</Typography>

                            <Typography variant="h6">Shipping Address:</Typography>
                            <Typography>
                                {selectedOrder.address.addressLine1}
                                {selectedOrder.address.addressLine2 && `, ${selectedOrder.address.addressLine2}`}
                            </Typography>
                            <Typography>
                                {selectedOrder.address.city}, {selectedOrder.address.state} {selectedOrder.address.zip}
                            </Typography>
                            {selectedOrder.address.businessName && <Typography>Business: {selectedOrder.address.businessName}</Typography>}
                            <Typography variant="h6">Products:</Typography>
                            {selectedOrder.cart.map((item, index) => (
                                <Typography key={index}>
                                    {item.name} - Quantity: {item.quantity}
                                    {item.selectedOption && item.selectedOption !== 'studyProgramOnly' && 
                                        <span style={{ marginLeft: '10px', fontStyle: 'italic', color: '#666' }}>
                                            ({item.displayName || item.selectedOption})
                                        </span>
                                    }
                                </Typography>
                            ))}
                            
                            {/* Shipping Labels Section */}
                            {selectedOrder.shipping && selectedOrder.shipping.labels && selectedOrder.shipping.labels.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ mt: 2 }}>Shipping Labels:</Typography>
                                    {selectedOrder.shipping.labels.map((label, index) => (
                                        <Paper key={index} sx={{ p: 2, mb: 1, backgroundColor: '#f5f5f5' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: label.type === 'outbound' ? '#2e7d32' : '#d32f2f' }}>
                                                {label.type === 'outbound' ? '📦 Outbound Label' : '↩️ Return Label'}
                                            </Typography>
                                            <Typography><strong>Tracking #:</strong> {label.tracking_number}</Typography>
                                            <Typography><strong>Carrier:</strong> {label.carrier_code.toUpperCase()}</Typography>
                                            <Typography><strong>Service:</strong> {label.service_code.replace(/_/g, ' ').toUpperCase()}</Typography>
                                            <Typography><strong>Cost:</strong> ${label.cost?.toFixed(2) || '0.00'}</Typography>
                                            <Typography><strong>Created:</strong> {new Date(label.created_at).toLocaleString()}</Typography>
                                            {label.label_download_url && (
                                                <Button 
                                                    variant="outlined" 
                                                    size="small" 
                                                    sx={{ mt: 1 }}
                                                    startIcon={<DownloadIcon />}
                                                    onClick={() => window.open(label.label_download_url, '_blank')}
                                                >
                                                    Download Label
                                                </Button>
                                            )}
                                        </Paper>
                                    ))}
                                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#666' }}>
                                        <strong>Shipping Cost:</strong> ${selectedOrder.shipping.cost?.toFixed(2) || '0.00'}
                                        {selectedOrder.shipping.returnCost > 0 && (
                                            <span> | <strong>Return Cost:</strong> ${selectedOrder.shipping.returnCost.toFixed(2)}</span>
                                        )}
                                        <span> | <strong>Total Shipping:</strong> ${selectedOrder.shipping.totalCost?.toFixed(2) || '0.00'}</span>
                                    </Typography>
                                </>
                            )}
                            
                            <Typography variant="h6" sx={{ mt: 2 }}>Subscription:</Typography>
                            {selectedOrder.subscription ? (
                                <div>
                                    <Typography>
                                        Start Date: {new Date(selectedOrder.subscription.startDate).toLocaleDateString()}
                                    </Typography>
                                    <Typography>End Date: {new Date(selectedOrder.subscription.endDate).toLocaleDateString()}</Typography>
                                    <Typography>
                                        Status: {selectedOrder.subscription.isSubscriptionActive ? 'Active' : 'Expired'}
                                    </Typography>
                                    <Button
                                        onClick={() => handleExtendSubscription(selectedOrder.subscription._id)}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Extend Subscription by 1 Month
                                    </Button>
                                </div>
                            ) : (
                                <Typography>No subscription associated with this order.</Typography>
                            )}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AdminOrders;
